import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, TrendingUp, TrendingDown, AlertTriangle, DollarSign,
  Users, Shield, Droplets, BarChart3, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://trading-api-1075463475276.us-central1.run.app';

interface PillarScore {
  growth: number;
  inflation: number;
  labor: number;
  policy: number;
  risk: number;
  liquidity: number;
}

interface RegimeData {
  regime: string;
  composite_score: number;
  pillars: PillarScore;
  multiplier: { confidence: number; max_positions: number; equity_pct: number };
  pillar_weights: Record<string, number>;
}

interface RecessionData {
  recession_probability: number;
  level: string;
  indicators: {
    yield_curve: number | null;
    weekly_claims: number | null;
    unemployment: number | null;
    vix: number | null;
  };
}

interface CrossAssetRatio {
  id: string;
  name: string;
  value: number;
  risk_signal: string;
  description: string;
}

interface CrossAssetData {
  ratios: CrossAssetRatio[];
  market_signal: string;
  risk_on_count: number;
  total_ratios: number;
  confirmation: string;
}

interface PillarIndicator {
  series_id: string;
  latest_value: number | null;
  latest_date: string | null;
  previous_value: number | null;
  trend: string;
}

interface PillarDetail {
  indicators: PillarIndicator[];
  score: number;
}

const REGIME_COLORS: Record<string, string> = {
  EXPANSION: '#22c55e',
  SLOWDOWN: '#f59e0b',
  CONTRACTION: '#ef4444',
  STAGFLATION: '#dc2626',
  CRISIS: '#991b1b',
};

const REGIME_BG: Record<string, string> = {
  EXPANSION: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)',
  SLOWDOWN: 'linear-gradient(135deg, #78350f 0%, #451a03 100%)',
  CONTRACTION: 'linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)',
  STAGFLATION: 'linear-gradient(135deg, #7f1d1d 0%, #3b0764 100%)',
  CRISIS: 'linear-gradient(135deg, #450a0a 0%, #1c1917 100%)',
};

const REGIME_DESCRIPTIONS: Record<string, string> = {
  EXPANSION: 'Strong growth, healthy labor, low risk — Full risk-on positioning',
  SLOWDOWN: 'Growth decelerating, mixed signals — Reduce position sizes',
  CONTRACTION: 'Declining growth, rising unemployment — Defensive positioning',
  STAGFLATION: 'Weak growth + high inflation — Minimal exposure, favor commodities',
  CRISIS: 'Market panic, liquidity freeze — Cash/hedges only',
};

const PILLAR_CONFIG = [
  { id: 'growth', label: 'Growth', icon: TrendingUp, color: '#22c55e', weight: '25%' },
  { id: 'inflation', label: 'Inflation', icon: DollarSign, color: '#f59e0b', weight: '20%' },
  { id: 'labor', label: 'Labor', icon: Users, color: '#3b82f6', weight: '20%' },
  { id: 'policy', label: 'Policy', icon: Shield, color: '#a855f7', weight: '15%' },
  { id: 'risk', label: 'Risk', icon: AlertTriangle, color: '#ef4444', weight: '10%' },
  { id: 'liquidity', label: 'Liquidity', icon: Droplets, color: '#06b6d4', weight: '10%' },
];

const SERIES_NAMES: Record<string, string> = {
  GDP: 'GDP (Nominal)', GDPC1: 'Real GDP', INDPRO: 'Industrial Production',
  RSAFS: 'Retail Sales', DGORDER: 'Durable Goods Orders',
  MANEMP: 'Manufacturing Employment', UMCSENT: 'Consumer Sentiment',
  CPIAUCSL: 'CPI (All Items)', CPILFESL: 'Core CPI', PCEPILFE: 'Core PCE',
  PCEPI: 'PCE Price Index', T5YIE: '5Y Breakeven Inflation',
  T10YIE: '10Y Breakeven Inflation', PPIACO: 'PPI (All Commodities)', GASREGW: 'Gasoline Price',
  UNRATE: 'Unemployment Rate', PAYEMS: 'Nonfarm Payrolls', ICSA: 'Initial Claims',
  CCSA: 'Continued Claims', JTSJOL: 'Job Openings', AWHAETP: 'Avg Weekly Hours',
  CES0500000003: 'Avg Hourly Earnings',
  FEDFUNDS: 'Fed Funds Rate', DFF: 'Effective Fed Funds', MORTGAGE30US: '30Y Mortgage Rate',
  VIXCLS: 'VIX', BAMLH0A0HYM2: 'HY Spread', T10Y2Y: '10Y-2Y Spread', T10Y3M: '10Y-3M Spread',
  M2SL: 'M2 Money Supply', WALCL: 'Fed Balance Sheet', TOTCI: 'Total C&I Loans',
  M1SL: 'M1 Money Supply', SOFR: 'SOFR Rate',
};

// Mini sparkline component for FRED series
function Sparkline({ data, color, width = 120, height = 32 }: { data: number[], color: string, width?: number, height?: number }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

// Yield Curve visualization
function YieldCurveChart({ yieldData }: { yieldData: Record<string, number> }) {
  const maturities = [
    { label: '3M', key: 'DGS3MO' }, { label: '6M', key: 'DGS6MO' },
    { label: '1Y', key: 'DGS1' }, { label: '2Y', key: 'DGS2' },
    { label: '5Y', key: 'DGS5' }, { label: '10Y', key: 'DGS10' },
    { label: '30Y', key: 'DGS30' }
  ];
  const points = maturities.filter(m => yieldData[m.key] != null);
  if (points.length < 3) return null;

  const w = 400, h = 140, pad = { t: 15, r: 20, b: 28, l: 45 };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const vals = points.map(p => yieldData[p.key]);
  const minY = Math.min(...vals) - 0.2;
  const maxY = Math.max(...vals) + 0.2;
  const rng = maxY - minY || 1;

  const linePoints = points.map((p, i) => {
    const x = pad.l + (i / (points.length - 1)) * cw;
    const y = pad.t + ch - ((yieldData[p.key] - minY) / rng) * ch;
    return `${x},${y}`;
  }).join(' ');

  // Check if inverted (short > long)
  const isInverted = vals[0] > vals[vals.length - 1];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth: 500, height: 'auto' }}>
      {[0, 0.5, 1].map(pct => {
        const y = pad.t + ch * (1 - pct);
        const val = minY + rng * pct;
        return (
          <g key={pct}>
            <line x1={pad.l} y1={y} x2={pad.l + cw} y2={y} stroke="#334155" strokeWidth="0.5" />
            <text x={pad.l - 6} y={y + 4} fill="#64748b" fontSize="9" textAnchor="end">{val.toFixed(1)}%</text>
          </g>
        );
      })}
      <polyline points={linePoints} fill="none" stroke={isInverted ? '#ef4444' : '#22c55e'} strokeWidth="2" />
      {points.map((p, i) => {
        const x = pad.l + (i / (points.length - 1)) * cw;
        const y = pad.t + ch - ((yieldData[p.key] - minY) / rng) * ch;
        return (
          <g key={p.key}>
            <circle cx={x} cy={y} r={3} fill={isInverted ? '#ef4444' : '#22c55e'} />
            <text x={x} y={h - 5} fill="#94a3b8" fontSize="9" textAnchor="middle">{p.label}</text>
            <text x={x} y={y - 8} fill="#d1d5db" fontSize="8" textAnchor="middle">{yieldData[p.key].toFixed(2)}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function Econ360Dashboard() {
  const [regime, setRegime] = useState<RegimeData | null>(null);
  const [recession, setRecession] = useState<RecessionData | null>(null);
  const [crossAsset, setCrossAsset] = useState<CrossAssetData | null>(null);
  const [pillarDetails, setPillarDetails] = useState<Record<string, PillarDetail> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPillar, setExpandedPillar] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [seriesHistory, setSeriesHistory] = useState<Record<string, number[]>>({});
  const [yieldCurveData, setYieldCurveData] = useState<Record<string, number>>({});
  const [drilldownSeries, setDrilldownSeries] = useState<{id: string; data: any[]} | null>(null);
  const [fedWatchData, setFedWatchData] = useState<any>(null);

  const fetchDrilldown = async (seriesId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/econ360/series/${seriesId}?limit=100`);
      const data = await res.json();
      if (data.data || data.series) {
        setDrilldownSeries({ id: seriesId, data: data.data || data.series || [] });
      }
    } catch { /* ignore */ }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regimeRes, recessionRes, crossRes, pillarRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/econ360/regime`).then(r => r.json()),
        fetch(`${API_BASE}/api/econ360/recession-probability`).then(r => r.json()),
        fetch(`${API_BASE}/api/econ360/cross-asset`).then(r => r.json()),
        fetch(`${API_BASE}/api/econ360/pillars`).then(r => r.json()),
      ]);

      if (regimeRes.status === 'fulfilled' && regimeRes.value.success) {
        setRegime(regimeRes.value);
      }
      if (recessionRes.status === 'fulfilled' && recessionRes.value.success) {
        setRecession(recessionRes.value);
      }
      if (crossRes.status === 'fulfilled' && crossRes.value.success) {
        setCrossAsset(crossRes.value);
      }
      if (pillarRes.status === 'fulfilled' && pillarRes.value.success) {
        setPillarDetails(pillarRes.value.pillars);
      }
      setLastUpdated(new Date());

      // Fetch yield curve data (parallel, non-blocking)
      const yieldSeries = ['DGS3MO', 'DGS6MO', 'DGS1', 'DGS2', 'DGS5', 'DGS10', 'DGS30'];
      Promise.all(
        yieldSeries.map(s =>
          fetch(`${API_BASE}/api/econ360/series/${s}?limit=1`).then(r => r.json()).catch(() => null)
        )
      ).then(results => {
        const yc: Record<string, number> = {};
        results.forEach((res, i) => {
          if (res?.success && res.data?.length > 0 && res.data[0].value != null) {
            yc[yieldSeries[i]] = res.data[0].value;
          }
        });
        setYieldCurveData(yc);
      });

      // Fetch sparkline history for key indicators (parallel, non-blocking)
      const sparkSeries = ['UNRATE', 'CPIAUCSL', 'FEDFUNDS', 'VIXCLS', 'M2SL', 'ICSA'];
      Promise.all(
        sparkSeries.map(s =>
          fetch(`${API_BASE}/api/econ360/series/${s}?limit=24`).then(r => r.json()).catch(() => null)
        )
      ).then(results => {
        const hist: Record<string, number[]> = {};
        results.forEach((res, i) => {
          if (res?.success && res.data?.length > 1) {
            hist[sparkSeries[i]] = res.data.filter((d: any) => d.value != null).map((d: any) => d.value).reverse();
          }
        });
        setSeriesHistory(hist);
      });

      // Fetch Fed Watch data
      fetch(`${API_BASE}/api/econ360/series/FEDFUNDS?limit=24`)
        .then(r => r.json())
        .then(d => {
          const points = d.data || d.series || [];
          if (points.length > 0) {
            setFedWatchData({
              currentRate: points[points.length - 1]?.value || points[0]?.value || 0,
              history: points.map((p: any) => p.value || 0),
              lastDate: points[points.length - 1]?.datetime || points[0]?.datetime || ''
            });
          }
        })
        .catch(() => {});

    } catch (err) {
      setError('Failed to load macro data');
      console.error('Econ360 fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const scoreColor = (score: number) => {
    if (score >= 70) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  if (loading && !regime) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <RefreshCw size={32} color="#a855f7" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#94a3b8', marginTop: 12 }}>Loading macro intelligence...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error && !regime) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <AlertTriangle size={32} color="#ef4444" />
        <p style={{ color: '#ef4444', marginTop: 12 }}>{error}</p>
        <button onClick={fetchData} style={{
          padding: '8px 16px', background: '#3b82f6', color: 'white',
          border: 'none', borderRadius: 6, cursor: 'pointer', marginTop: 8
        }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)', minHeight: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: 'white', fontSize: 28, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={30} color="#a855f7" />
            Macro 360 Intelligence
          </h2>
          <p style={{ color: '#94a3b8', margin: '4px 0 0', fontSize: 14 }}>
            Six-Pillar Regime Classification powered by 30+ FRED indicators
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && (
            <span style={{ color: '#64748b', fontSize: 12 }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button onClick={fetchData} style={{
            padding: '8px 16px', background: '#1e293b', color: '#94a3b8',
            border: '1px solid #334155', borderRadius: 8, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13
          }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Regime Banner */}
      {regime && (
        <div style={{
          background: REGIME_BG[regime.regime] || REGIME_BG.SLOWDOWN,
          borderRadius: 16, padding: 24, marginBottom: 24,
          border: `2px solid ${REGIME_COLORS[regime.regime] || '#f59e0b'}`,
          boxShadow: `0 0 30px ${REGIME_COLORS[regime.regime] || '#f59e0b'}20`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 2 }}>
                Current Macro Regime
              </div>
              <div style={{ fontSize: 42, fontWeight: 'bold', color: REGIME_COLORS[regime.regime], letterSpacing: 3 }}>
                {regime.regime}
              </div>
              <p style={{ color: '#cbd5e1', margin: '8px 0 0', fontSize: 15, maxWidth: 500 }}>
                {REGIME_DESCRIPTIONS[regime.regime]}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 2 }}>Composite Score</div>
              <div style={{ fontSize: 48, fontWeight: 'bold', color: scoreColor(regime.composite_score) }}>
                {regime.composite_score}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>/ 100</div>
              <div style={{ marginTop: 8 }}>
                <span style={{
                  background: '#1e293b', padding: '4px 12px', borderRadius: 20,
                  fontSize: 13, color: REGIME_COLORS[regime.regime]
                }}>
                  Confidence: {regime.multiplier.confidence}x
                </span>
              </div>
            </div>
          </div>

          {/* Position Sizing */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 20,
            background: 'rgba(0,0,0,0.3)', borderRadius: 10, padding: 14
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#94a3b8', fontSize: 11 }}>Max Positions</div>
              <div style={{ color: 'white', fontSize: 22, fontWeight: 'bold' }}>{regime.multiplier.max_positions}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#94a3b8', fontSize: 11 }}>Equity Allocation</div>
              <div style={{ color: 'white', fontSize: 22, fontWeight: 'bold' }}>{regime.multiplier.equity_pct}%</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#94a3b8', fontSize: 11 }}>Signal Multiplier</div>
              <div style={{ color: 'white', fontSize: 22, fontWeight: 'bold' }}>{regime.multiplier.confidence}x</div>
            </div>
          </div>
        </div>
      )}

      {/* Six Pillars Grid */}
      {regime && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: 'white', fontSize: 18, marginBottom: 14 }}>Six-Pillar Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {PILLAR_CONFIG.map(pillar => {
              const score = regime.pillars[pillar.id as keyof PillarScore] ?? 0;
              const detail = pillarDetails?.[pillar.id];
              const isExpanded = expandedPillar === pillar.id;
              const Icon = pillar.icon;

              return (
                <div key={pillar.id} style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  borderRadius: 12, border: '1px solid #334155', overflow: 'hidden'
                }}>
                  <div
                    onClick={() => setExpandedPillar(isExpanded ? null : pillar.id)}
                    style={{ padding: 16, cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon size={20} color={pillar.color} />
                        <span style={{ color: 'white', fontWeight: 'bold', fontSize: 15 }}>{pillar.label}</span>
                        <span style={{ color: '#64748b', fontSize: 11 }}>({pillar.weight})</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: scoreColor(score), fontWeight: 'bold', fontSize: 20 }}>{score}</span>
                        {isExpanded ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
                      </div>
                    </div>

                    {/* Score Bar */}
                    <div style={{ width: '100%', height: 6, background: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${score}%`, height: '100%',
                        background: `linear-gradient(90deg, ${pillar.color}80, ${pillar.color})`,
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>

                  {/* Expanded Indicators */}
                  {isExpanded && detail && (
                    <div style={{ borderTop: '1px solid #1e293b', padding: '12px 16px', background: '#0a0e1a' }}>
                      {detail.indicators.map(ind => (
                        <div key={ind.series_id} onClick={() => fetchDrilldown(ind.series_id)} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 0', borderBottom: '1px solid #1e293b20', cursor: 'pointer'
                        }}>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>
                            {SERIES_NAMES[ind.series_id] || ind.series_id}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {seriesHistory[ind.series_id] && (
                              <Sparkline data={seriesHistory[ind.series_id]} color={pillar.color} width={60} height={20} />
                            )}
                            <span style={{ color: 'white', fontSize: 13, fontWeight: 500 }}>
                              {ind.latest_value != null ? ind.latest_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                            </span>
                            {ind.trend === 'rising' && <TrendingUp size={12} color="#22c55e" />}
                            {ind.trend === 'falling' && <TrendingDown size={12} color="#ef4444" />}
                          </div>
                        </div>
                      ))}
                      {detail.indicators.length > 0 && detail.indicators[0].latest_date && (
                        <div style={{ color: '#475569', fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                          Latest: {new Date(detail.indicators[0].latest_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Yield Curve + Key Indicator Trends */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 2fr)', gap: 20, marginBottom: 24 }}>
        {/* Yield Curve */}
        {Object.keys(yieldCurveData).length >= 3 && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 12, padding: 20, border: '1px solid #334155'
          }}>
            <h3 style={{ color: 'white', fontSize: 16, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={18} color="#06b6d4" />
              Treasury Yield Curve
            </h3>
            <YieldCurveChart yieldData={yieldCurveData} />
            <div style={{ marginTop: 8, display: 'flex', gap: 12, justifyContent: 'center' }}>
              {yieldCurveData.DGS2 != null && yieldCurveData.DGS10 != null && (
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 'bold',
                  background: yieldCurveData.DGS10 > yieldCurveData.DGS2 ? '#065f4620' : '#7f1d1d30',
                  color: yieldCurveData.DGS10 > yieldCurveData.DGS2 ? '#22c55e' : '#ef4444',
                  border: `1px solid ${yieldCurveData.DGS10 > yieldCurveData.DGS2 ? '#22c55e40' : '#ef444440'}`
                }}>
                  10Y-2Y: {(yieldCurveData.DGS10 - yieldCurveData.DGS2).toFixed(2)}% {yieldCurveData.DGS10 > yieldCurveData.DGS2 ? 'NORMAL' : 'INVERTED'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Key Indicator Trends */}
        {Object.keys(seriesHistory).length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 12, padding: 20, border: '1px solid #334155'
          }}>
            <h3 style={{ color: 'white', fontSize: 16, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={18} color="#a855f7" />
              Key Indicator Trends (24-period)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { id: 'UNRATE', label: 'Unemployment', color: '#3b82f6', unit: '%' },
                { id: 'CPIAUCSL', label: 'CPI Index', color: '#f59e0b', unit: '' },
                { id: 'FEDFUNDS', label: 'Fed Funds', color: '#a855f7', unit: '%' },
                { id: 'VIXCLS', label: 'VIX', color: '#ef4444', unit: '' },
                { id: 'M2SL', label: 'M2 Money Supply', color: '#06b6d4', unit: 'B' },
                { id: 'ICSA', label: 'Initial Claims', color: '#22c55e', unit: '' },
              ].filter(s => seriesHistory[s.id]?.length > 1).map(s => {
                const vals = seriesHistory[s.id];
                const latest = vals[vals.length - 1];
                const prev = vals[vals.length - 2];
                const change = latest - prev;
                return (
                  <div key={s.id} style={{ background: '#0f172a', borderRadius: 8, padding: 12 }}>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4 }}>{s.label}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <div>
                        <span style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>
                          {latest >= 10000 ? `${(latest / 1000).toFixed(0)}k` : latest >= 100 ? latest.toFixed(0) : latest.toFixed(2)}
                        </span>
                        <span style={{ color: '#64748b', fontSize: 11, marginLeft: 2 }}>{s.unit}</span>
                      </div>
                      <span style={{ color: change >= 0 ? '#22c55e' : '#ef4444', fontSize: 11, fontWeight: '600' }}>
                        {change >= 0 ? '+' : ''}{latest >= 10000 ? `${(change / 1000).toFixed(1)}k` : change >= 100 ? change.toFixed(0) : change.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Sparkline data={vals} color={s.color} width={160} height={28} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Row: Recession Probability + Cross-Asset Ratios */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 2fr)', gap: 20 }}>
        {/* Recession Probability */}
        {recession && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 12, padding: 20, border: '1px solid #334155'
          }}>
            <h3 style={{ color: 'white', fontSize: 16, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={18} color="#f59e0b" />
              Recession Probability
            </h3>

            {/* Gauge */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{
                width: 140, height: 70, margin: '0 auto', position: 'relative',
                borderTopLeftRadius: 70, borderTopRightRadius: 70,
                overflow: 'hidden', background: '#0f172a'
              }}>
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  height: 70, transformOrigin: 'bottom center',
                  background: `conic-gradient(
                    from 180deg,
                    ${recession.recession_probability > 50 ? '#ef4444' : recession.recession_probability > 25 ? '#f59e0b' : '#22c55e'} 0deg,
                    ${recession.recession_probability > 50 ? '#ef4444' : recession.recession_probability > 25 ? '#f59e0b' : '#22c55e'} ${recession.recession_probability * 1.8}deg,
                    #1e293b ${recession.recession_probability * 1.8}deg
                  )`,
                  borderTopLeftRadius: 70, borderTopRightRadius: 70,
                }} />
                <div style={{
                  position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                  width: 100, height: 50, background: '#0f172a',
                  borderTopLeftRadius: 50, borderTopRightRadius: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <span style={{
                    fontSize: 28, fontWeight: 'bold',
                    color: recession.recession_probability > 50 ? '#ef4444' : recession.recession_probability > 25 ? '#f59e0b' : '#22c55e'
                  }}>
                    {recession.recession_probability}%
                  </span>
                </div>
              </div>
              <div style={{
                marginTop: 8, padding: '4px 12px', display: 'inline-block',
                borderRadius: 20, fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase',
                background: recession.level === 'low' ? '#065f4620' : recession.level === 'moderate' ? '#78350f40' : '#7f1d1d40',
                color: recession.level === 'low' ? '#22c55e' : recession.level === 'moderate' ? '#f59e0b' : '#ef4444',
                border: `1px solid ${recession.level === 'low' ? '#22c55e40' : recession.level === 'moderate' ? '#f59e0b40' : '#ef444440'}`
              }}>
                {recession.level} risk
              </div>
            </div>

            {/* Key Indicators */}
            <div style={{ display: 'grid', gap: 8 }}>
              {[
                { label: 'Yield Curve (10Y-2Y)', value: recession.indicators.yield_curve, unit: '%', warn: (v: number) => v < 0 },
                { label: 'Weekly Claims', value: recession.indicators.weekly_claims, unit: '', fmt: (v: number) => v.toLocaleString(), warn: (v: number) => v > 300000 },
                { label: 'Unemployment', value: recession.indicators.unemployment, unit: '%', warn: (v: number) => v > 5 },
                { label: 'VIX', value: recession.indicators.vix, unit: '', warn: (v: number) => v > 25 },
              ].map(item => (
                <div key={item.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 10px', background: '#0f172a', borderRadius: 6
                }}>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{item.label}</span>
                  <span style={{
                    color: item.value != null && item.warn(item.value) ? '#ef4444' : '#22c55e',
                    fontSize: 13, fontWeight: 'bold'
                  }}>
                    {item.value != null ? (item.fmt ? item.fmt(item.value) : `${item.value.toFixed(2)}${item.unit}`) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cross-Asset Ratios */}
        {crossAsset && (
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 12, padding: 20, border: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: 'white', fontSize: 16, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarChart3 size={18} color="#3b82f6" />
                Cross-Asset Validation
              </h3>
              <div style={{
                padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 'bold',
                background: crossAsset.market_signal === 'RISK_ON' ? '#065f4630' :
                  crossAsset.market_signal === 'RISK_OFF' ? '#7f1d1d30' : '#78350f30',
                color: crossAsset.market_signal === 'RISK_ON' ? '#22c55e' :
                  crossAsset.market_signal === 'RISK_OFF' ? '#ef4444' : '#f59e0b',
                border: `1px solid ${crossAsset.market_signal === 'RISK_ON' ? '#22c55e40' :
                  crossAsset.market_signal === 'RISK_OFF' ? '#ef444440' : '#f59e0b40'}`
              }}>
                {crossAsset.market_signal} ({crossAsset.confirmation})
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {crossAsset.ratios.map(ratio => (
                <div key={ratio.id} style={{
                  background: '#0f172a', borderRadius: 8, padding: 14,
                  border: `1px solid ${ratio.risk_signal === 'RISK_ON' ? '#22c55e20' : '#ef444420'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>{ratio.name}</span>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                      background: ratio.risk_signal === 'RISK_ON' ? '#065f4640' : '#7f1d1d40',
                      color: ratio.risk_signal === 'RISK_ON' ? '#22c55e' : '#ef4444'
                    }}>
                      {ratio.risk_signal}
                    </span>
                  </div>
                  <div style={{ color: 'white', fontSize: 20, fontWeight: 'bold' }}>
                    {typeof ratio.value === 'number' ? ratio.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : ratio.value}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>{ratio.description}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fed Watch Section */}
      {fedWatchData && (
        <div style={{ background: '#1e293b80', borderRadius: '12px', padding: '16px', border: '1px solid #334155', marginTop: '16px' }}>
          <h3 style={{ color: '#a855f7', margin: '0 0 12px', fontSize: '16px' }}>Fed Watch</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Current Fed Funds Rate</div>
              <div style={{ color: '#a855f7', fontSize: '28px', fontWeight: 700 }}>{Number(fedWatchData.currentRate).toFixed(2)}%</div>
            </div>
            <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Rate History (24 periods)</div>
              <Sparkline data={fedWatchData.history} color="#a855f7" width={160} height={40} />
            </div>
            <div style={{ background: '#0f172a', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '12px' }}>Last Updated</div>
              <div style={{ color: 'white', fontSize: '14px', fontWeight: 600, marginTop: '8px' }}>{fedWatchData.lastDate}</div>
            </div>
          </div>
        </div>
      )}

      {/* Drill-Down Modal */}
      {drilldownSeries && (
        <div onClick={() => setDrilldownSeries(null)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: 16, padding: 28, border: '1px solid #334155',
            minWidth: 660, maxWidth: '90vw', boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: 20 }}>
                {SERIES_NAMES[drilldownSeries.id] || drilldownSeries.id}
              </h3>
              <button onClick={() => setDrilldownSeries(null)} style={{
                background: '#334155', border: 'none', color: 'white', borderRadius: 8,
                width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>X</button>
            </div>
            {(() => {
              const pts = drilldownSeries.data.filter((d: any) => d.value != null);
              if (pts.length < 2) return <div style={{ color: '#94a3b8' }}>No data available</div>;
              const values = pts.map((d: any) => d.value as number);
              const minV = Math.min(...values);
              const maxV = Math.max(...values);
              const rangeV = maxV - minV || 1;
              const chartW = 600, chartH = 250;
              const padL = 60, padR = 20, padT = 20, padB = 40;
              const innerW = chartW - padL - padR;
              const innerH = chartH - padT - padB;
              const linePts = pts.map((d: any, i: number) => {
                const x = padL + (i / (pts.length - 1)) * innerW;
                const y = padT + innerH - ((d.value - minV) / rangeV) * innerH;
                return `${x},${y}`;
              }).join(' ');
              // Y-axis labels (5 ticks)
              const yTicks = [0, 0.25, 0.5, 0.75, 1].map(pct => ({
                val: minV + rangeV * pct,
                y: padT + innerH * (1 - pct)
              }));
              // X-axis labels (up to 6)
              const step = Math.max(1, Math.floor(pts.length / 5));
              const xLabels = pts.filter((_: any, i: number) => i % step === 0 || i === pts.length - 1);
              return (
                <svg width={chartW} height={chartH} style={{ display: 'block', margin: '0 auto' }}>
                  {yTicks.map((t, i) => (
                    <g key={i}>
                      <line x1={padL} y1={t.y} x2={padL + innerW} y2={t.y} stroke="#334155" strokeWidth="0.5" />
                      <text x={padL - 8} y={t.y + 4} fill="#94a3b8" fontSize="10" textAnchor="end">
                        {t.val >= 10000 ? `${(t.val / 1000).toFixed(0)}k` : t.val >= 100 ? t.val.toFixed(0) : t.val.toFixed(2)}
                      </text>
                    </g>
                  ))}
                  <polyline points={linePts} fill="none" stroke="#a855f7" strokeWidth="2" />
                  {xLabels.map((d: any, i: number) => {
                    const idx = pts.indexOf(d);
                    const x = padL + (idx / (pts.length - 1)) * innerW;
                    const dateStr = d.datetime ? new Date(d.datetime).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) : '';
                    return (
                      <text key={i} x={x} y={chartH - 8} fill="#64748b" fontSize="9" textAnchor="middle">{dateStr}</text>
                    );
                  })}
                </svg>
              );
            })()}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: 12 }}>
              <span>{drilldownSeries.data.length} data points</span>
              <span>Series: {drilldownSeries.id}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
